import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import { create_routing_request } from "../../api/routing_requests";
import { capitalize } from "../lib/functions";
import AddressEndpointFields, { addressSnapshotFields, emptyAddressEndpoint, endpointFromAddress, isCompleteAddress } from './AddressEndpointFields';
import "../css/SelectJobModal.css"; // Reuse existing styles for consistency

const RequestJobModal = ({
  theme,
  openRequestJob,
  setOpenRequestJob,
  customers,
  addressBook,
  date,
  onSubmitSuccess,
  canSaveAddress,
  onAddressSaved,
}) => {
  const dropdownRef = useRef(null);
  
  // Form states
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [collection, setCollection] = useState(emptyAddressEndpoint());
  const [delivery, setDelivery] = useState(emptyAddressEndpoint());
  const [route, setRoute] = useState("");
  const [customRoute, setCustomRoute] = useState("");
  const [showCustomRouteInput, setShowCustomRouteInput] = useState(false);
  const [pallets, setPallets] = useState("");
  const [weight, setWeight] = useState("");
  const [bookedTime, setBookedTime] = useState("");
  const [comments, setComments] = useState("");
  
  // UI states
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showList, setShowList] = useState(false);
  const [manualInput, setManualInput] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Get route names for the selected delivery point
  const availableRoutes = React.useMemo(() => {
    if (selectedCustomer !== "") {
      const d = customers[selectedCustomer];
      if (d?.route_name) return [d.route_name];
    }
    return Array.from(new Set(customers.map(c => c.route_name).filter(Boolean))).sort();
  }, [selectedCustomer, customers]);

  // Filter customers based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredCustomers([]);
    } else {
      const filtered = Object.entries(customers).filter(([_, d]) =>
        (d.customer_company || d.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (d.route_name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, [searchTerm, customers]);

  // Handle click outside searchable customer list to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle customer selection from searchable dropdown
  const handleSelectCustomer = (key) => {
    const d = customers[key];
    setSelectedCustomer(key);
    setCollection(endpointFromAddress(d.collection_address_details));
    setDelivery(d.delivery_address_details
      ? endpointFromAddress(d.delivery_address_details)
      : {
          address_book_id: d.delivery_address_id || null,
          name: d.company_name || d.customer_company || '',
          address: d.address || '',
          postcode: d.postcode || '',
          additional_info: '',
        });
    setSearchTerm(d.company_name || d.customer_company || '');
    setShowList(false);
    setError("");

    if (d.route_name) {
      if (availableRoutes.includes(d.route_name)) {
        setRoute(d.route_name);
        setShowCustomRouteInput(false);
      } else {
        setRoute("custom_other");
        setCustomRoute(d.route_name);
        setShowCustomRouteInput(true);
      }
    } else {
      setRoute("");
      setShowCustomRouteInput(false);
    }
  };

  // Toggle manual customer input mode
  const handleManualInputToggle = (e) => {
    const isManual = e.target.checked;
    setManualInput(isManual);
    setSelectedCustomer("");
    setCollection(emptyAddressEndpoint());
    setDelivery(emptyAddressEndpoint());
    setRoute("");
    setCustomRoute("");
    setShowCustomRouteInput(false);
    setSearchTerm("");
    setError("");
  };

  // Handle Route dropdown changes
  const handleRouteDropdownChange = (e) => {
    const val = e.target.value;
    setRoute(val);
    if (val === "custom_other") {
      setShowCustomRouteInput(true);
    } else {
      setShowCustomRouteInput(false);
      setCustomRoute("");
    }
  };

  // Validate all required fields
  const isFormValid = () => {
    // 1. Customer verification
    if (!manualInput && !selectedCustomer) return false;
    if (!isCompleteAddress(collection) || !isCompleteAddress(delivery)) return false;

    // 2. Route verification
    if (!route) return false;
    if (route === "custom_other" && !customRoute.trim()) return false;

    // 3. At least pallets or weight
    const cleanPallets = pallets.toString().replace(/[^0-9]/g, "");
    const cleanWeight = weight.toString().replace(/[^0-9]/g, "");
    if (!cleanPallets && !cleanWeight) return false;

    return true;
  };

  // Submit handler
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Check main validation
    if (!isFormValid()) {
      setError("Please complete all required fields (customer/company details, route, and at least pallets or weight).");
      return;
    }

    // Must have at least pallets or weight (strip any non-digits like commas, spaces, or units)
    const cleanPallets = pallets !== "" ? pallets.toString().replace(/[^0-9]/g, "") : "";
    const cleanWeight = weight !== "" ? weight.toString().replace(/[^0-9]/g, "") : "";

    const pVal = cleanPallets !== "" ? parseInt(cleanPallets, 10) : null;
    const wVal = cleanWeight !== "" ? parseInt(cleanWeight, 10) : null;

    if (pVal === null && wVal === null) {
      setError("At least Pallets or Weight in kg must be specified.");
      return;
    }

    if (pVal !== null && isNaN(pVal)) {
      setError("Pallets count must be a valid number.");
      return;
    }

    if (wVal !== null && isNaN(wVal)) {
      setError("Weight must be a valid number.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    const finalRoute = route === "custom_other" ? customRoute.trim() : route;
    const snapshots = addressSnapshotFields(collection, delivery);

    const requestPayload = {
      ...snapshots,
      date,
      company: snapshots.delivery_name,
      address: snapshots.delivery_address,
      postcode: snapshots.delivery_postcode,
      route: finalRoute ? capitalize(finalRoute) : null,
      pallets: pVal,
      weight: wVal,
      booked_time: bookedTime || null,
      comments: comments.trim() || null,
      // new-system link — only set when a delivery point was selected
      customer_id: (!manualInput && selectedCustomer !== '') ? (customers[selectedCustomer]?.customer_id ?? null) : null,
      customer_deliveries_id: (!manualInput && selectedCustomer !== '') ? (customers[selectedCustomer]?.id ?? null) : null,
      customer: (!manualInput && selectedCustomer !== '') ? (customers[selectedCustomer]?.customer_company ?? null) : null,
    };

    const res = await create_routing_request(requestPayload);
    setIsSubmitting(false);

    if (res.success) {
      setOpenRequestJob(false);
      if (onSubmitSuccess) onSubmitSuccess(res.data);
    } else {
      setError(res.error || "Failed to submit request.");
    }
  };

  return (
    <Modal
      id={theme}
      isOpen={openRequestJob}
      onRequestClose={() => setOpenRequestJob(false)}
      ariaHideApp={false}
      style={{
        overlay: {
          zIndex: 99999,
          backgroundColor: "rgba(0, 0, 0, 0.55)",
        },
        content: {
          margin: "auto",
          justifyContent: "center",
          display: "flex",
          backgroundColor: "transparent",
          border: "none",
          zIndex: 100000,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <div className="job-modal-container" style={{ height: "auto", maxHeight: "90vh" }}>
          
          {/* Header */}
          <div className="flex items-center justify-between" style={{ marginTop: "10px", marginBottom: "15px" }}>
            <h2 style={{ margin: 0, fontSize: "1.5em", fontWeight: "bold" }}>Request Job Creation</h2>
            <div style={{ fontSize: "14px", opacity: 0.8 }}>
              Date: <strong>{date ? new Date(date).toLocaleDateString() : ""}</strong>
            </div>
          </div>

          {/* Mode Toggle */}
          <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "15px" }}>
            <input
              type="checkbox"
              id="manualToggle"
              checked={manualInput}
              onChange={handleManualInputToggle}
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
            <label htmlFor="manualToggle" style={{ fontSize: "14px", cursor: "pointer" }}>
              Manually Enter Customer Details
            </label>
          </div>

          {/* Search Dropdown or Manual Company Name */}
          {!manualInput ? (
            <div className="select-group" style={{ position: "relative", marginBottom: "15px" }} ref={dropdownRef}>
              <label htmlFor="customerSearch" style={{ fontWeight: "600", fontSize: "14px" }}>Choose a customer:</label>
              <input
                id="customerSearch"
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedCustomer("");
                  setShowList(true);
                }}
                placeholder="Start typing customer/company name..."
                autoComplete="off"
                onFocus={() => setShowList(true)}
                className="input"
                style={{ marginBottom: "5px" }}
              />
              
              {showList && filteredCustomers.length > 0 && (
                <ul className="dropdown-list" style={{ maxHeight: "200px" }}>
                  {filteredCustomers.map(([key, customer]) => (
                    <li
                      key={key}
                      onMouseDown={() => handleSelectCustomer(key)}
                      className="dropdown-item"
                    >
                      <span style={{ fontWeight: 600 }}>{customer.customer_company}</span>
                      {(customer.company_name && customer.company_name !== customer.customer_company) && (
                        <span style={{ opacity: 0.7, marginLeft: 4 }}>· {customer.company_name}</span>
                      )}
                      {customer.route_name && (
                        <span style={{ opacity: 0.6, marginLeft: 4, fontSize: '0.9em' }}>({customer.route_name})</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          <AddressEndpointFields label="Collection" addresses={addressBook} value={collection} onChange={setCollection} canSaveAddress={canSaveAddress} onAddressSaved={onAddressSaved} />
          <AddressEndpointFields label="Delivery" addresses={addressBook} value={delivery} onChange={setDelivery} canSaveAddress={canSaveAddress} onAddressSaved={onAddressSaved} />

          {/* Route Dropdown Selection */}
          <div className="select-group" style={{ marginBottom: "15px" }}>
            <label htmlFor="routeSelect" style={{ fontWeight: "600", fontSize: "14px" }}>Designated Route:</label>
            <select
              id="routeSelect"
              value={route}
              onChange={handleRouteDropdownChange}
              style={{ padding: "10px", fontSize: "15px" }}
            >
              <option value="">-- Select a Route --</option>
              <option value="custom_other">-- Manually Enter Route Name --</option>
              {availableRoutes.map(rName => (
                <option key={rName} value={rName}>{rName}</option>
              ))}
            </select>
          </div>

          {/* Custom Route Input if selected Custom */}
          {showCustomRouteInput && (
            <div className="input-group" style={{ marginBottom: "15px" }}>
              <label htmlFor="customRoute" style={{ fontWeight: "600", fontSize: "14px" }}>Enter Custom Route Name:</label>
              <input
                id="customRoute"
                type="text"
                className="input"
                value={customRoute}
                onChange={(e) => setCustomRoute(e.target.value)}
                placeholder="Enter custom route..."
                style={{ marginBottom: "5px" }}
              />
            </div>
          )}

          {/* Pallets & Weight Stack (At least one must be filled) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "15px" }}>
            <div className="input-group">
              <label htmlFor="pallets" style={{ fontWeight: "600", fontSize: "14px" }}>Pallets (qty):</label>
              <input
                id="pallets"
                type="text"
                className="input"
                value={pallets}
                onChange={(e) => setPallets(e.target.value)}
                placeholder="Pallet count (e.g. 10)"
                style={{ marginBottom: "5px" }}
              />
            </div>
            <div className="input-group">
              <label htmlFor="weight" style={{ fontWeight: "600", fontSize: "14px" }}>Weight (kg):</label>
              <input
                id="weight"
                type="text"
                className="input"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="Total weight in kg (e.g. 5,000)"
                style={{ marginBottom: "5px" }}
              />
            </div>
          </div>

          {/* Delivery Booking Time (Optional) */}
          <div className="input-group" style={{ marginBottom: "15px" }}>
            <label htmlFor="bookedTime" style={{ fontWeight: "600", fontSize: "14px" }}>Delivery Booking Time (Optional):</label>
            <input
              id="bookedTime"
              type="time"
              className="input"
              value={bookedTime}
              onChange={(e) => setBookedTime(e.target.value)}
              style={{ marginBottom: "5px" }}
            />
          </div>

          {/* Comments Box */}
          <div className="input-group" style={{ marginBottom: "15px" }}>
            <label htmlFor="comments" style={{ fontWeight: "600", fontSize: "14px" }}>Comments / Notes:</label>
            <textarea
              id="comments"
              className="input"
              rows="3"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Provide comments, loading notes, or reference details..."
              style={{ 
                height: "80px", 
                resize: "none", 
                fontFamily: "inherit", 
                padding: "8px 12px", 
                marginBottom: "5px",
                borderRadius: "4px"
              }}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message" style={{ margin: "10px 0", padding: "8px", borderRadius: "4px", backgroundColor: "rgba(220, 53, 69, 0.15)", border: "1px solid red" }}>
              {error}
            </div>
          )}

          {/* Button Footer */}
          <div className="button-group" style={{ marginTop: "15px", padding: "0" }}>
            <button
              type="button"
              className="add-button"
              onClick={() => setOpenRequestJob(false)}
              style={{ backgroundColor: "#6c757d" }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="add-button"
              disabled={isSubmitting || !isFormValid()}
              style={{
                backgroundColor: (isSubmitting || !isFormValid()) ? "#a0a0a0" : "#4caf50",
                cursor: (isSubmitting || !isFormValid()) ? "not-allowed" : "pointer",
                transition: "all 0.2s ease"
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </button>
          </div>

        </div>
      </form>
    </Modal>
  );
};

export default RequestJobModal;
